// Stale-site alerting (S5-2, plan §3.5: "cloud alert when a node reports backlog > N or offline
// > 30 min"). Rendered by ReportsLayout so it rides above every report page: whenever a site in
// the caller's scope has gone stale, the figures below might be reading a Cloud copy that is
// behind the till, and this says so before anyone trusts a stale number.
//
// It renders nothing when the whole fleet is fresh — an always-present "all good" bar is chrome
// nobody reads. The signal is `GET /reports/freshness`, polled on a slow cadence (this is
// operational metadata, not a live tile).
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { reportsApi } from '@verlo/shared/services/api';
import { formatDataThrough } from './freshness';

const POLL_MS = 60_000;

const StaleSiteBanner = () => {
  const [stale, setStale] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await reportsApi.getFreshness();
        if (!alive) return;
        const sites = res.data?.sites || [];
        setStale(sites.filter((s) => s.stale));
      } catch {
        // A freshness read that itself fails is not worth a banner of its own — the reports
        // below will surface a real outage on their own. Leave the last-known state in place.
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!stale.length) return null;

  return (
    <div
      className="mb-5 flex items-start gap-3 rounded-lg border border-signal-warn-line bg-signal-warn-tint px-4 py-3"
      data-testid="stale-site-banner"
      role="alert"
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-signal-warn" />
      <div className="text-[12px] text-signal-warn">
        <p className="font-semibold">
          {stale.length === 1
            ? '1 site is not syncing, so its figures may be stale'
            : `${stale.length} sites are not syncing, so their figures may be stale`}
        </p>
        <ul className="mt-1 space-y-0.5 text-signal-warn">
          {stale.map((s) => (
            <li key={s.site_id} data-testid={`stale-site-${s.site_id}`}>
              <span className="font-medium">{s.site_name || s.site_id}</span>
              {': '}
              {s.reasons?.length ? s.reasons.join(', ') : 'stale'}
              <span className="opacity-70"> · data through {formatDataThrough(s.last_seen_at)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default StaleSiteBanner;
